import React, { useState, useEffect } from 'react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  FunnelIcon,
  TruckIcon,
  HomeIcon,
  SparklesIcon,
  WrenchScrewdriverIcon,
  CubeIcon
} from '@heroicons/react/24/outline';
import { useMarketplace } from '../../../hooks/useMarketplace';
import MarketplaceAssetCard from './MarketplaceAssetCard';
import MarketplaceAssetForm from './MarketplaceAssetForm';
import useAuthStore from '../../../store/useAuthStore';

const MarketplaceTab = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [page, setPage] = useState(1);
  const { assets, loading, fetchAssets, createAsset } = useMarketplace();
  const { isAuthenticated } = useAuthStore();
  
  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);
  
  const categories = [
    { id: 'all', label: 'All Assets', icon: CubeIcon, emoji: '📦' },
    { id: 'vehicle', label: 'Vehicles', icon: TruckIcon, emoji: '🚗' },
    { id: 'yacht', label: 'Yachts', icon: null, emoji: '⛵' },
    { id: 'property', label: 'Real Estate', icon: HomeIcon, emoji: '🏠' },
    { id: 'luxury', label: 'Luxury Items', icon: SparklesIcon, emoji: '💎' },
    { id: 'equipment', label: 'Equipment', icon: WrenchScrewdriverIcon, emoji: '🔧' },
    { id: 'other', label: 'Other', icon: CubeIcon, emoji: '📦' }
  ];
  
  const handleSearch = (categoryOverride = null) => {
    setPage(1);
    const params = {};
    const categoryToUse = categoryOverride !== null ? categoryOverride : selectedCategory;
    
    if (categoryToUse !== 'all') {
      params.category = categoryToUse;
    }
    
    if (searchTerm.trim()) {
      params.search = searchTerm.trim();
    }
    
    if (priceRange.min) {
      params.min_price = priceRange.min;
    }
    
    if (priceRange.max) {
      params.max_price = priceRange.max;
    }
    
    fetchAssets(params);
  };
  
  const handleCreateAsset = async (assetData) => {
    await createAsset(assetData);
    setPage(1);
    setShowCreateForm(false);
  };
  
  const filteredAssets = assets;
  const totalPages = Math.ceil(filteredAssets.length / 8);
  const currentPage = Math.min(page, Math.max(1, totalPages));
  const visibleAssets = filteredAssets.slice((currentPage - 1) * 8, currentPage * 8);
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gray-900 border border-[#2A356D] rounded-xl p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center space-x-2">
              <span className="text-3xl">🏪</span>
              <span>Goods & Services Marketplace</span>
            </h2>
            <p className="text-white mt-1">Trade BTC1 for physical assets</p>
          </div>
          
          {isAuthenticated && (
            <button
              onClick={() => setShowCreateForm(true)}
              className="px-6 py-3 bg-[#0E1696] hover:bg-[#0E1696] !text-white rounded-lg transition-all flex items-center space-x-2 font-semibold shadow-xl hover:scale-105"
            >
              <PlusIcon className="h-5 w-5" />
              <span>Create Asset Ad</span>
            </button>
          )}
        </div>
      </div>
      
      {/* Category Pills */}
      <div className="flex flex-wrap gap-2">
        {categories.map((category) => (
          <button
            key={category.id}
            onClick={() => {
              setSelectedCategory(category.id);
              handleSearch(category.id);
            }}
            className={`px-4 py-2 rounded-lg transition-all flex items-center space-x-2 ${
              selectedCategory === category.id
                ? 'bg-[#0E1696] !text-white shadow-lg'
                : 'bg-gray-800 text-white hover:bg-gray-700'
            }`}
          >
            <span className="text-lg">{category.emoji}</span>
            <span className="text-sm font-medium">{category.label}</span>
          </button>
        ))}
      </div>
      
      {/* Search and Filters */}
      <div className="bg-gray-800 rounded-xl p-4 space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white pointer-events-none" />
            <input
              type="text"
              placeholder="Search by title, location, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              className="w-full pl-4 !pr-10 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors flex items-center space-x-2"
          >
            <FunnelIcon className="h-5 w-5" />
            <span>Filters</span>
          </button>
          
          <button
            onClick={() => handleSearch()}
            className="px-6 py-3 bg-green-600 hover:bg-purple-700 text-white rounded-lg transition-colors font-medium"
          >
            Search
          </button>
        </div>
        
        {/* Advanced Filters */}
        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-gray-700">
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Min Price (BTC1)
              </label>
              <input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={priceRange.min}
                onChange={(e) => setPriceRange({ ...priceRange, min: e.target.value })}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Max Price (BTC1)
              </label>
              <input
                type="number"
                step="0.01"
                placeholder="999.99"
                value={priceRange.max}
                onChange={(e) => setPriceRange({ ...priceRange, max: e.target.value })}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>
        )}
      </div>
      
      {/* Assets Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
        </div>
      ) : filteredAssets.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {visibleAssets.map((asset) => (
            <MarketplaceAssetCard key={asset.asset_id} asset={asset} />
          ))}
        </div>
      ) : (
        <div className="bg-gray-800 rounded-xl p-12 text-center">
          <div className="text-6xl mb-4">🔍</div>
          <h3 className="text-xl font-semibold text-white mb-2">No Assets Found</h3>
          <p className="text-white">
            {selectedCategory !== 'all' 
              ? `No ${categories.find(c => c.id === selectedCategory)?.label.toLowerCase()} available` 
              : 'No marketplace listings available'}
          </p>
          {isAuthenticated && (
            <button
              onClick={() => setShowCreateForm(true)}
              className="mt-6 px-6 py-3 bg-green-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
            >
              Be the first to post!
            </button>
          )}
        </div>
      )}
      
      {!loading && totalPages > 1 && (
        <nav aria-label="Marketplace pagination" className="flex flex-wrap items-center justify-center gap-4">
          <button type="button" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)} className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg disabled:opacity-40 disabled:cursor-not-allowed">Previous</button>
          <span className="text-sm text-white">Page {currentPage} of {totalPages}</span>
          <button type="button" disabled={currentPage === totalPages} onClick={() => setPage(currentPage + 1)} className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
        </nav>
      )}

      {/* Create Asset Modal */}
      {showCreateForm && (
        <MarketplaceAssetForm
          onClose={() => setShowCreateForm(false)}
          onSubmit={handleCreateAsset}
        />
      )}
    </div>
  );
};

export default MarketplaceTab;

